<template>
	<div class="statistics-page">
		<StatisticsTabs
			:value="selectedMainTab"
			@input="selectMainTab"
		/>

		<div v-if="selectedMainTab === 'legacy'" class="legacy-building-select">
			<CustomSelect
				:value="selectedLegacyBuildingOption"
				:options="legacyBuildingSelectOptions"
				:placeholder="$t('toppanel.selectBuilding')"
				:loading="legacyLoadingBuildings"
				@select="setLegacyBuilding"
				@search="fetchLegacyBuildingOptions"
			/>
		</div>

		<div v-if="selectedMainTab === 'overview'" class="statistics-overview">
			<div
				class="statistics-compare-layout"
				:class="{ active: compareMode, 'has-overview-detail': selectedOverviewRow }"
			>
				<div class="statistics-search-column">
					<StatisticsFilterPanel
						ref="overviewFilter"
						:selected-customer="selectedOverviewCustomer"
						:structure-dropdown-open="structureDropdownOpen"
						:structure-search.sync="structureSearch"
						:structure-options="visibleStructureOptions"
						:loading-tree="overviewLoadingTree"
						:is-structure-expanded="isStructureExpanded"
						:start.sync="overviewStart"
						:stop.sync="overviewStop"
						:building-filter-id.sync="selectedOverviewBuildingFilterId"
						:building-options="overviewBuildingOptions"
						:loading-buildings="overviewLoadingBuildings"
						:loading="overviewLoading"
						:compare-mode="compareMode"
						show-compare-button
						show-print-button
						@toggle-compare="toggleCompareMode"
						@print="printStatistics"
						@toggle-structure-dropdown="toggleStructureDropdown"
						@structure-option-click="handleStructureOptionClick"
						@select-customer="selectOverviewCustomer"
						@toggle-structure-node="toggleStructureNode"
						@search="fetchOverview"
					/>

					<StatisticsChartPanel
						:selected-customer="selectedOverviewCustomer"
						:start="overviewStart"
						:stop="overviewStop"
						:format-date-range="formatDateRange"
						:display-mode="overviewDisplayMode"
						:sort-direction="overviewSortDirection"
						:sort-options="overviewSortOptions"
						:selected-sort-option="selectedOverviewSortOption"
						:loading="overviewLoading"
						:error="overviewError"
						:rows="visibleOverviewRows"
						:selected-row="selectedOverviewRow"
						:is-row-expanded="isOverviewRowExpanded"
						:get-row-height="getOverviewRowHeight"
						:get-track-height="getOverviewTrackHeight"
						:get-segment-width="getOverviewSegmentWidth"
						:get-handled-label="getOverviewHandledFlawLabel"
						:get-unhandled-label="getOverviewUnhandledFlawLabel"
						@set-display-mode="setOverviewDisplayMode"
						@toggle-sort-direction="toggleOverviewSortDirection"
						@set-sort-by="setOverviewSortBy"
						@toggle-row="toggleOverviewRow"
						@select-row="selectOverviewRow"
					/>

					<StatisticsDetailLayout
						v-if="compareMode && selectedOverviewRow"
						:row="selectedOverviewRow"
						:inventory="selectedOverviewInventory"
						:visible-inventory-rows="getVisibleInventoryRows(selectedOverviewInventory, 'overview')"
						:selected-inventory-item="selectedOverviewInventoryItem"
						:flaws="selectedOverviewInventoryFlaws"
						:layout-class="getDetailLayoutClass('overview')"
						:inventory-panel-class="getDetailPanelClass('overview', 'inventory')"
						:flaws-panel-class="getDetailPanelClass('overview', 'flaws')"
						:inventory-expanded="isDetailPanelExpanded('overview', 'inventory')"
						:flaws-expanded="isDetailPanelExpanded('overview', 'flaws')"
						:inventory-expand-title="getDetailPanelExpandTitle('overview', 'inventory')"
						:flaws-expand-title="getDetailPanelExpandTitle('overview', 'flaws')"
						:all-inventory-groups-expanded="areAllInventoryGroupsExpanded('overview', selectedOverviewInventory)"
						:map-loading="selectedOverviewMapLoading"
						:map-image="selectedOverviewMapImage"
						:map-error="selectedOverviewMapError"
						:get-inventory-name="getInventoryName"
						:get-flaw-name="getFlawName"
						:get-inventory-map-title="getInventoryMapTitle"
						:has-inventory-map-position="hasInventoryMapPosition"
						:get-inventory-map-marker-style="getInventoryMapMarkerStyle"
						:get-inventory-map-icon-class="getInventoryMapIconClass"
						:is-inventory-group-expanded="isOverviewInventoryGroupExpanded"
						@toggle-panel="toggleDetailPanelExpansion('overview', $event)"
						@toggle-all-inventory-groups="toggleAllInventoryGroups('overview', selectedOverviewInventory)"
						@inventory-row-click="handleInventoryRowClick($event, 'overview')"
						@open-map="openInventoryMapModal($event, 'overview')"
					/>
				</div>

				<StatisticsDetailLayout
					v-if="!compareMode && selectedOverviewRow"
					:row="selectedOverviewRow"
					:inventory="selectedOverviewInventory"
					:visible-inventory-rows="getVisibleInventoryRows(selectedOverviewInventory, 'overview')"
					:selected-inventory-item="selectedOverviewInventoryItem"
					:flaws="selectedOverviewInventoryFlaws"
					:layout-class="getDetailLayoutClass('overview')"
					overview-detail
					:inventory-panel-class="getDetailPanelClass('overview', 'inventory')"
					:flaws-panel-class="getDetailPanelClass('overview', 'flaws')"
					:inventory-expanded="isDetailPanelExpanded('overview', 'inventory')"
					:flaws-expanded="isDetailPanelExpanded('overview', 'flaws')"
					:inventory-expand-title="getDetailPanelExpandTitle('overview', 'inventory')"
					:flaws-expand-title="getDetailPanelExpandTitle('overview', 'flaws')"
					:all-inventory-groups-expanded="areAllInventoryGroupsExpanded('overview', selectedOverviewInventory)"
					:map-loading="selectedOverviewMapLoading"
					:map-image="selectedOverviewMapImage"
					:map-error="selectedOverviewMapError"
					:get-inventory-name="getInventoryName"
					:get-flaw-name="getFlawName"
					:get-inventory-map-title="getInventoryMapTitle"
					:has-inventory-map-position="hasInventoryMapPosition"
					:get-inventory-map-marker-style="getInventoryMapMarkerStyle"
					:get-inventory-map-icon-class="getInventoryMapIconClass"
					:is-inventory-group-expanded="isOverviewInventoryGroupExpanded"
					@toggle-panel="toggleDetailPanelExpansion('overview', $event)"
					@toggle-all-inventory-groups="toggleAllInventoryGroups('overview', selectedOverviewInventory)"
					@inventory-row-click="handleInventoryRowClick($event, 'overview')"
					@open-map="openInventoryMapModal($event, 'overview')"
				/>

				<div v-if="compareMode" class="statistics-search-column">
					<StatisticsFilterPanel
						ref="compareFilter"
						:selected-customer="compareSelectedCustomer"
						:structure-dropdown-open="compareStructureDropdownOpen"
						:structure-search.sync="compareStructureSearch"
						:structure-options="compareVisibleStructureOptions"
						:loading-tree="overviewLoadingTree"
						:is-structure-expanded="isCompareStructureExpanded"
						:start.sync="compareStart"
						:stop.sync="compareStop"
						:building-filter-id.sync="compareBuildingFilterId"
						:building-options="compareBuildingOptions"
						:loading-buildings="compareLoadingBuildings"
						:loading="compareLoading"
						@toggle-structure-dropdown="toggleCompareStructureDropdown"
						@structure-option-click="handleCompareStructureOptionClick"
						@select-customer="selectCompareCustomer"
						@toggle-structure-node="toggleCompareStructureNode"
						@search="fetchCompareOverview"
					/>

					<StatisticsChartPanel
						:selected-customer="compareSelectedCustomer"
						:start="compareStart"
						:stop="compareStop"
						:format-date-range="formatDateRange"
						:display-mode="compareDisplayMode"
						:sort-direction="compareSortDirection"
						:sort-options="compareSortOptions"
						:selected-sort-option="selectedCompareSortOption"
						:loading="compareLoading"
						:error="compareError"
						:rows="visibleCompareRows"
						:selected-row="compareSelectedRow"
						:is-row-expanded="isCompareRowExpanded"
						:get-row-height="getOverviewRowHeight"
						:get-track-height="getOverviewTrackHeight"
						:get-segment-width="getCompareSegmentWidth"
						:get-handled-label="getCompareHandledFlawLabel"
						:get-unhandled-label="getCompareUnhandledFlawLabel"
						@set-display-mode="setCompareDisplayMode"
						@toggle-sort-direction="toggleCompareSortDirection"
						@set-sort-by="setCompareSortBy"
						@toggle-row="toggleCompareRow"
						@select-row="selectCompareRow"
					/>

					<StatisticsDetailLayout
						v-if="compareSelectedRow"
						:row="compareSelectedRow"
						:inventory="compareSelectedInventory"
						:visible-inventory-rows="getVisibleInventoryRows(compareSelectedInventory, 'compare')"
						:selected-inventory-item="compareSelectedInventoryItem"
						:flaws="compareSelectedInventoryFlaws"
						:layout-class="getDetailLayoutClass('compare')"
						:inventory-panel-class="getDetailPanelClass('compare', 'inventory')"
						:flaws-panel-class="getDetailPanelClass('compare', 'flaws')"
						:inventory-expanded="isDetailPanelExpanded('compare', 'inventory')"
						:flaws-expanded="isDetailPanelExpanded('compare', 'flaws')"
						:inventory-expand-title="getDetailPanelExpandTitle('compare', 'inventory')"
						:flaws-expand-title="getDetailPanelExpandTitle('compare', 'flaws')"
						:all-inventory-groups-expanded="areAllInventoryGroupsExpanded('compare', compareSelectedInventory)"
						:map-loading="compareSelectedMapLoading"
						:map-image="compareSelectedMapImage"
						:map-error="compareSelectedMapError"
						:get-inventory-name="getInventoryName"
						:get-flaw-name="getFlawName"
						:get-inventory-map-title="getInventoryMapTitle"
						:has-inventory-map-position="hasInventoryMapPosition"
						:get-inventory-map-marker-style="getInventoryMapMarkerStyle"
						:get-inventory-map-icon-class="getInventoryMapIconClass"
						:is-inventory-group-expanded="isCompareInventoryGroupExpanded"
						@toggle-panel="toggleDetailPanelExpansion('compare', $event)"
						@toggle-all-inventory-groups="toggleAllInventoryGroups('compare', compareSelectedInventory)"
						@inventory-row-click="handleInventoryRowClick($event, 'compare')"
						@open-map="openInventoryMapModal($event, 'compare')"
					/>
				</div>
			</div>
		</div>

		<LegacyStatisticsPanel
			v-else
			:selected-tab="selectedTab"
			:flaws-done="getFlawsDone"
			:flaws-undone="getFlawsUndone"
			:labels="labels"
			:category-labels="categoryLabels"
			:colors="colors"
			:round-planning-statistics="roundPlanningStatistics"
			:is-mobile="isMobile"
			:year="year"
			:years="years"
			:info-box="infoBox"
			:pie-data="pieData"
			:pie-labels="pieLabels"
			:pie-colors="pieColors"
			:get-color="getColor"
			@set-year="setCurrentYear"
			@select-tab="selectTab"
		/>

		<StatisticsMapModal
			:open="mapModalOpen"
			:item="getMapModalItem()"
			:image="getMapModalImage()"
			:loading="getMapModalLoading()"
			:error="getMapModalError()"
			:title="getMapModalTitle()"
			:get-inventory-name="getInventoryName"
			:has-inventory-map-position="hasInventoryMapPosition"
			:get-inventory-map-marker-style="getInventoryMapMarkerStyle"
			:get-inventory-map-icon-class="getInventoryMapIconClass"
			@close="closeInventoryMapModal"
		/>
	</div>
</template>

<script>
import CustomSelect from '@/components/CustomSelect';
import iconMixin from '@/mixins/iconMixin.js';
import { mapActions, mapGetters } from 'vuex';
import LegacyStatisticsPanel from './LegacyStatisticsPanel.vue';
import StatisticsChartPanel from './StatisticsChartPanel.vue';
import StatisticsDetailLayout from './StatisticsDetailLayout.vue';
import StatisticsFilterPanel from './StatisticsFilterPanel.vue';
import StatisticsMapModal from './StatisticsMapModal.vue';
import StatisticsTabs from './StatisticsTabs.vue';

export default {
	mixins: [iconMixin],

	components: {
		CustomSelect,
		LegacyStatisticsPanel,
		StatisticsChartPanel,
		StatisticsDetailLayout,
		StatisticsFilterPanel,
		StatisticsMapModal,
		StatisticsTabs
	},

	data() {
		return {
			fixed: [],
			roundCount: [],
			colors: ['#7bca78', '#da6060'],
			years: [],
			selectedMainTab: 'overview',
			selectedTab: 0,
			year: null,

			flawStatistics: {},
			roundPlanningStatistics: [],
			legacyBuildingOptions: [],
			legacyLoadingBuildings: false,

			compareMode: false,
			customerTree: [],
			selectedOverviewCustomer: null,
			structureDropdownOpen: false,
			structureSearch: '',
			expandedStructureIds: [],
			overviewDisplayMode: 'count',
			overviewSortBy: 'unhandledFlaws',
			overviewSortDirection: 'desc',
			overviewStart: null,
			overviewStop: null,
			overviewRows: [],
			expandedOverviewRowIds: [],
			selectedOverviewRow: null,
			selectedOverviewBuilding: null,
			selectedOverviewInventory: [],
			selectedOverviewInventoryItem: null,
			expandedOverviewInventoryGroupIds: [],
			expandedOverviewDetailPanels: {
				inventory: false,
				flaws: false
			},
			selectedOverviewMapImage: '',
			selectedOverviewMapLoading: false,
			selectedOverviewMapError: '',
			overviewBuildingOptions: [],
			selectedOverviewBuildingFilterId: '',
			overviewLoading: false,
			overviewLoadingTree: false,
			overviewLoadingBuildings: false,
			overviewError: '',

			compareSelectedCustomer: null,
			compareStructureDropdownOpen: false,
			compareStructureSearch: '',
			compareExpandedStructureIds: [],
			compareDisplayMode: 'count',
			compareSortBy: 'unhandledFlaws',
			compareSortDirection: 'desc',
			compareStart: null,
			compareStop: null,
			compareRows: [],
			compareExpandedRowIds: [],
			compareSelectedRow: null,
			compareSelectedInventory: [],
			compareSelectedInventoryItem: null,
			compareExpandedInventoryGroupIds: [],
			compareExpandedDetailPanels: {
				inventory: false,
				flaws: false
			},
			compareSelectedMapImage: '',
			compareSelectedMapLoading: false,
			compareSelectedMapError: '',
			compareBuildingOptions: [],
			compareBuildingFilterId: '',
			compareLoading: false,
			compareLoadingBuildings: false,
			compareError: '',
			mapModalOpen: false,
			mapModalTarget: 'overview',
			isPrintingStatistics: false,
			printRestoreState: null,
			mapImageCache: {}
		};
	},

	computed: {
		...mapGetters('user', ['getActiveCustomer', 'getActiveCompany', 'getUser']),
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('buildingStore', ['selectedBuilding', 'getUserBuildingId']),

		legacyBuildingSelectOptions() {
			return [
				this.$isAllowedTo('top_panel_fetch_buildings_without_user_id') && {
					id: null,
					name: this.$t('toppanel.allBuildings')
				},
				{
					id: '__userBuildingId__',
					name: this.$t('toppanel.myBuildings')
				},
				...this.legacyBuildingOptions
			].filter(Boolean);
		},

		selectedLegacyBuildingOption() {
			if (!this.selectedBuilding?.id && this.getUserBuildingId !== null) {
				return {
					id: '__userBuildingId__',
					name: this.$t('toppanel.myBuildings')
				};
			}

			if (!this.selectedBuilding?.id) {
				return {
					id: null,
					name: this.$t('toppanel.allBuildings')
				};
			}

			return this.selectedBuilding;
		},

		maxOverviewCount() {
			return this.flattenOverviewRows(this.overviewRows)
				.reduce((max, row) => Math.max(max, row.totalCount), 0);
		},

		visibleOverviewRows() {
			if (this.isPrintingStatistics) {
				return this.flattenOverviewRows(this.overviewRows);
			}

			return this.flattenVisibleOverviewRows(this.overviewRows);
		},

		selectedOverviewInventoryFlaws() {
			return this.selectedOverviewInventoryItem?.flaws || [];
		},

		compareSelectedInventoryFlaws() {
			return this.compareSelectedInventoryItem?.flaws || [];
		},

		overviewSortOptions() {
			if (this.overviewDisplayMode === 'percent') {
				return [
					{ value: 'unhandledFlawPercent', name: this.$t('statistics.unhandled_share') },
					{ value: 'handledFlawPercent', name: this.$t('statistics.handled_share') },
					{ value: 'name', name: this.$t('statistics.name') }
				];
			}

			return [
				{ value: 'unhandledFlaws', name: this.$t('statistics.unhandled') },
				{ value: 'handledFlaws', name: this.$t('statistics.handled') },
				{ value: 'name', name: this.$t('statistics.name') }
			];
		},

		selectedOverviewSortOption() {
			return this.overviewSortOptions.find(option => option.value === this.overviewSortBy) || this.overviewSortOptions[0];
		},

		compareSortOptions() {
			if (this.compareDisplayMode === 'percent') {
				return [
					{ value: 'unhandledFlawPercent', name: this.$t('statistics.unhandled_share') },
					{ value: 'handledFlawPercent', name: this.$t('statistics.handled_share') },
					{ value: 'name', name: this.$t('statistics.name') }
				];
			}

			return [
				{ value: 'unhandledFlaws', name: this.$t('statistics.unhandled') },
				{ value: 'handledFlaws', name: this.$t('statistics.handled') },
				{ value: 'name', name: this.$t('statistics.name') }
			];
		},

		selectedCompareSortOption() {
			return this.compareSortOptions.find(option => option.value === this.compareSortBy) || this.compareSortOptions[0];
		},

		compareVisibleStructureOptions() {
			const query = this.compareStructureSearch.trim().toLowerCase();

			if (query) {
				return this.flattenStructureOptions(this.customerTree)
					.filter(item => item.name.toLowerCase().includes(query));
			}

			return this.getVisibleCompareStructureOptions(this.customerTree);
		},

		maxCompareCount() {
			return this.flattenOverviewRows(this.compareRows)
				.reduce((max, row) => Math.max(max, row.totalCount), 0);
		},

		visibleCompareRows() {
			if (this.isPrintingStatistics) {
				return this.flattenOverviewRows(this.compareRows);
			}

			return this.flattenVisibleCompareRows(this.compareRows);
		},

		visibleStructureOptions() {
			const query = this.structureSearch.trim().toLowerCase();

			if (query) {
				return this.flattenStructureOptions(this.customerTree)
					.filter(item => item.name.toLowerCase().includes(query));
			}

			return this.getVisibleStructureOptions(this.customerTree);
		},

		getFlawsUndone() {
			return this.flawStatistics.flawCounts
				? this.flawStatistics.flawCounts.map(count => count.flaws, [])
				: [];
		},

		getFlawsDone() {
			return this.flawStatistics.flawCounts
				? this.flawStatistics.flawCounts.map(count => count.doneFlaws, [])
				: [];
		},

		pieData() {
			if (this.selectedTab === 1) {
				return [
					this.getControllerStatisticsData.completed,
					this.getControllerStatisticsData.todo,
					this.getControllerStatisticsData.ongoing,
					this.getControllerStatisticsData.ongoingExpired
				];
			}

			return [
				this.flawStatistics.totalFlawsDone,
				this.flawStatistics.totalFlawsNotCompleted
			];
		},

		categoryLabels() {
			const _ = this.$i18n.locale;
			return [
			this.$t('statistics.completed'),
			this.$t('statistics.not_completed')
			];
		},

		pieLabels() {
			const _ = this.$i18n.locale;
			return this.selectedTab
			? [
				this.$t('statistics.completed'),
				this.$t('statistics.not_completed'),
				this.$t('statistics.ongoing'),
				this.$t('statistics.started_but_expired'),
				]
			: this.categoryLabels;
		},

		pieColors() {
			return this.selectedTab ? ['#7bca78', '#da6060', '#ffe000', '#ff9500'] : this.colors;
		},

		infoBox() {
			this.getRoundHistoryCount();

			return !this.selectedTab
			? this.$t('statistics.total_flaws', {
				count: this.flawStatistics.totalFlaws,
				fixed: this.flawStatistics.totalFlawsDone
				})
			: `${this.comDoneText}
				${this.comOngoingText}
				${this.comIncompleteText}
				${this.comStartedButExpiredText}`;
		},

		getControllerStatisticsData() {
			return {
				completed: this.roundCount[0],
				ongoing: this.roundCount[1],
				todo: this.roundCount[2],
				ongoingExpired: this.roundCount[3]
			};
		},

		comDoneText() {
			return this.roundCount[0] === 1
				? this.$t('statistics.done_inspection', { count: this.roundCount[0] })
				: this.$t('statistics.done_inspections', { count: this.roundCount[0] });
		},

		comOngoingText() {
			return this.roundCount[1] === 1
				? this.$t('statistics.ongoing_inspection', { count: this.roundCount[1] })
				: this.$t('statistics.ongoing_inspections', { count: this.roundCount[1] });
		},

		comIncompleteText() {
			return this.roundCount[2] === 1
				? this.$t('statistics.incomplete_inspection', { count: this.roundCount[2] })
				: this.$t('statistics.incomplete_inspections', { count: this.roundCount[2] });
		},

		comStartedButExpiredText() {
			return this.roundCount[3] === 1
				? this.$t('statistics.expired_inspection', { count: this.roundCount[3] })
				: this.$t('statistics.expired_inspections', { count: this.roundCount[3] });
		},

		getTriggerValue() {
			return `${this.getActiveCustomer?.id}-${this.selectedBuilding?.id}-${this.getUserBuildingId}`;
		},
		labels() {
			const _ = this.$i18n.locale; 
			return [
				this.$t('statistics.january'),
				this.$t('statistics.february'),
				this.$t('statistics.march'),
				this.$t('statistics.april'),
				this.$t('statistics.may'),
				this.$t('statistics.june'),
				this.$t('statistics.july'),
				this.$t('statistics.august'),
				this.$t('statistics.september'),
				this.$t('statistics.october'),
				this.$t('statistics.november'),
				this.$t('statistics.december')
			];
		}
	},

	watch: {
		overviewSortBy() {
			this.sortOverviewTree();
		},
		compareSortBy() {
			this.sortCompareTree();
		},
		'getActiveCompany.id'() {
			this.fetchOverviewTree();
		},
		'getActiveCustomer.id'() {
			this.setDefaultOverviewCustomer();
			this.fetchLegacyBuildingOptions();
			this.setLegacyBuilding({
				id: null,
				name: this.$t('toppanel.allBuildings')
			});
		},
		getTriggerValue() {
			this.refreshData();
		},
		labels(newLabels) {
			if (this.chart) {
			this.chart.data.labels = newLabels;
			this.chart.update();
			}
		},
		chartData(newData) {
			if (this.chart) {
			this.chart.data.datasets[0].data = newData;
			this.chart.update();
			}
		}
	},

	methods: {
		...mapActions('fileStore', ['getFile']),
		...mapActions('buildingStore', ['setSelectedBuilding', 'setUserBuildingId']),

		selectMainTab(tab) {
			this.selectedMainTab = tab;

			if (tab === 'legacy') {
				this.fetchLegacyBuildingOptions();
				this.refreshData();
			}
		},

		async fetchLegacyBuildingOptions(search = '') {
			if (!this.getActiveCustomer?.id) {
				this.legacyBuildingOptions = [];

				return;
			}

			this.legacyLoadingBuildings = true;

			try {
				const buildings = await this.$services.building.getBuildings({
					customerId: this.getActiveCustomer.id,
					name: search,
					orderBy: 'Name',
					currentPage: 1,
					pageSize: 5000
				});

				this.legacyBuildingOptions = Array.isArray(buildings) ? buildings : [];
			} finally {
				this.legacyLoadingBuildings = false;
			}
		},

		setLegacyBuilding(building) {
			if (building?.id === '__userBuildingId__') {
				this.setSelectedBuilding({ id: null });
				this.setUserBuildingId(this.getUser?.id);

				return;
			}

			this.setUserBuildingId(null);
			this.setSelectedBuilding(building || { id: null });
		},

		formatDateRange(start, stop) {
			if (start && stop) return `${start} - ${stop}`;
			if (start) return this.$t('statistics.from_date', { date: start });
			if (stop) return this.$t('statistics.to_date', { date: stop });

			return '';
		},

		toggleCompareMode() {
			this.compareMode = !this.compareMode;

			if (this.compareMode && !this.compareSelectedCustomer) {
				this.compareSelectedCustomer = this.selectedOverviewCustomer
					? { ...this.selectedOverviewCustomer }
					: null;
				this.compareStart = this.overviewStart;
				this.compareStop = this.overviewStop;
				this.compareExpandedStructureIds = [...this.expandedStructureIds];

				if (this.compareSelectedCustomer) {
					this.fetchCompareBuildingOptions();
				}
			}
		},

		toggleCompareStructureDropdown() {
			this.compareStructureDropdownOpen = !this.compareStructureDropdownOpen;

			if (this.compareStructureDropdownOpen) {
				this.$nextTick(() => {
					this.$refs.compareFilter?.focusStructureSearch();
				});
			}
		},

		toggleStructureDropdown() {
			this.structureDropdownOpen = !this.structureDropdownOpen;

			if (this.structureDropdownOpen) {
				this.$nextTick(() => {
					this.$refs.overviewFilter?.focusStructureSearch();
				});
			}
		},

		toggleStructureNode(id) {
			if (this.expandedStructureIds.includes(id)) {
				this.expandedStructureIds = this.expandedStructureIds.filter(openId => openId !== id);

				return;
			}

			this.expandedStructureIds = [...this.expandedStructureIds, id];
		},

		isStructureExpanded(id) {
			return this.expandedStructureIds.includes(id);
		},

		toggleCompareStructureNode(id) {
			if (this.compareExpandedStructureIds.includes(id)) {
				this.compareExpandedStructureIds = this.compareExpandedStructureIds.filter(openId => openId !== id);

				return;
			}

			this.compareExpandedStructureIds = [...this.compareExpandedStructureIds, id];
		},

		isCompareStructureExpanded(id) {
			return this.compareExpandedStructureIds.includes(id);
		},

		handleCompareStructureOptionClick(item) {
			if (item.children.length) {
				this.toggleCompareStructureNode(item.id);

				return;
			}

			this.selectCompareCustomer(item);
		},

		async selectCompareCustomer(customer) {
			this.compareSelectedCustomer = customer || null;
			this.compareStructureDropdownOpen = false;
			this.compareStructureSearch = '';
			this.compareRows = [];
			this.compareExpandedRowIds = [];
			this.compareSelectedRow = null;
			this.compareSelectedInventory = [];
			this.compareSelectedInventoryItem = null;
			this.compareExpandedInventoryGroupIds = [];
			this.resetDetailPanelExpansion('compare');
			this.resetSelectedMapPreview('compare');
			this.compareBuildingFilterId = '';
			this.compareBuildingOptions = [];

			if (this.compareSelectedCustomer) {
				await this.fetchCompareBuildingOptions();
			}
		},

		setOverviewDisplayMode(mode) {
			this.overviewDisplayMode = mode;
			this.overviewSortBy = mode === 'percent' ? 'unhandledFlawPercent' : 'unhandledFlaws';
			this.overviewSortDirection = 'desc';
			this.sortOverviewTree();
		},

		toggleOverviewSortDirection() {
			this.overviewSortDirection = this.overviewSortDirection === 'desc' ? 'asc' : 'desc';
			this.sortOverviewTree();
		},

		setOverviewSortBy(option) {
			this.overviewSortBy = option.value;
		},

		setCompareDisplayMode(mode) {
			this.compareDisplayMode = mode;
			this.compareSortBy = mode === 'percent' ? 'unhandledFlawPercent' : 'unhandledFlaws';
			this.compareSortDirection = 'desc';
			this.sortCompareTree();
		},

		toggleCompareSortDirection() {
			this.compareSortDirection = this.compareSortDirection === 'desc' ? 'asc' : 'desc';
			this.sortCompareTree();
		},

		setCompareSortBy(option) {
			this.compareSortBy = option.value;
		},

		getExpandableRowIds(rows) {
			return this.flattenOverviewRows(rows)
				.filter(row => row.children?.length)
				.map(row => row.id);
		},

		getInventoryGroupIds(inventory) {
			return this.getGroupedInventory(inventory).map(group => group.id);
		},

		prepareStatisticsPrint() {
			if (this.printRestoreState) return;

			this.printRestoreState = {
				isPrintingStatistics: this.isPrintingStatistics,
				expandedOverviewRowIds: [...this.expandedOverviewRowIds],
				compareExpandedRowIds: [...this.compareExpandedRowIds],
				expandedOverviewInventoryGroupIds: [...this.expandedOverviewInventoryGroupIds],
				compareExpandedInventoryGroupIds: [...this.compareExpandedInventoryGroupIds],
				expandedOverviewDetailPanels: { ...this.expandedOverviewDetailPanels },
				compareExpandedDetailPanels: { ...this.compareExpandedDetailPanels },
				structureDropdownOpen: this.structureDropdownOpen,
				compareStructureDropdownOpen: this.compareStructureDropdownOpen,
				mapModalOpen: this.mapModalOpen
			};

			this.isPrintingStatistics = true;
			this.structureDropdownOpen = false;
			this.compareStructureDropdownOpen = false;
			this.mapModalOpen = false;
			this.expandedOverviewRowIds = this.getExpandableRowIds(this.overviewRows);
			this.compareExpandedRowIds = this.getExpandableRowIds(this.compareRows);

			if (this.selectedOverviewRow) {
				this.expandedOverviewDetailPanels = { inventory: true, flaws: true };
			}

			if (this.compareSelectedRow) {
				this.compareExpandedDetailPanels = { inventory: true, flaws: true };
			}
		},

		restoreStatisticsPrint() {
			if (!this.printRestoreState) return;

			const state = this.printRestoreState;
			this.isPrintingStatistics = state.isPrintingStatistics;
			this.expandedOverviewRowIds = state.expandedOverviewRowIds;
			this.compareExpandedRowIds = state.compareExpandedRowIds;
			this.expandedOverviewInventoryGroupIds = state.expandedOverviewInventoryGroupIds;
			this.compareExpandedInventoryGroupIds = state.compareExpandedInventoryGroupIds;
			this.expandedOverviewDetailPanels = state.expandedOverviewDetailPanels;
			this.compareExpandedDetailPanels = state.compareExpandedDetailPanels;
			this.structureDropdownOpen = state.structureDropdownOpen;
			this.compareStructureDropdownOpen = state.compareStructureDropdownOpen;
			this.mapModalOpen = state.mapModalOpen;
			this.printRestoreState = null;
		},

		printStatistics() {
			this.prepareStatisticsPrint();

			this.$nextTick(() => {
				window.print();
			});
		},

		handleStructureOptionClick(item) {
			if (item.children.length) {
				this.toggleStructureNode(item.id);

				return;
			}

			this.selectOverviewCustomer(item);
		},

		async selectOverviewCustomer(customer) {
			this.selectedOverviewCustomer = customer || null;
			this.structureDropdownOpen = false;
			this.structureSearch = '';
			this.overviewRows = [];
			this.expandedOverviewRowIds = [];
			this.selectedOverviewRow = null;
			this.selectedOverviewBuilding = null;
			this.selectedOverviewInventory = [];
			this.selectedOverviewInventoryItem = null;
			this.expandedOverviewInventoryGroupIds = [];
			this.resetDetailPanelExpansion('overview');
			this.resetSelectedMapPreview('overview');
			this.selectedOverviewBuildingFilterId = '';
			this.overviewBuildingOptions = [];

			if (this.selectedOverviewCustomer) {
				await this.fetchOverviewBuildingOptions();
			}
		},

		setDefaultOverviewCustomer() {
			if (!this.getActiveCustomer) return;

			const match = this.findCustomerById(this.customerTree, this.getActiveCustomer.id);
			this.selectedOverviewCustomer = match || { ...this.getActiveCustomer };
			this.expandedStructureIds = this.selectedOverviewCustomer
				? this.$utils.tree.getPathIds(this.customerTree, this.selectedOverviewCustomer.id)
				: [];
			this.fetchOverviewBuildingOptions();
		},

		async fetchOverviewTree() {
			this.overviewError = '';
			this.overviewLoadingTree = true;

			try {
				let rootNode = null;

				if (!this.$isAllowedTo('tree_view_full') && this.$isAllowedTo('tree_see_own_root')) {
					rootNode = await this.$services.customer.getRootCustomer({
						customerId: this.getUser.customerId,
						companyId: this.getActiveCompany?.id
					});
				}

				this.customerTree = await this.$services.customer.getTree({
					companyNode: this.getActiveCompany?.id,
					parentNode: this.$isAllowedTo('tree_view_full')
						? null
						: rootNode
							? rootNode.id
							: this.getUser.customerId
				});

				this.setDefaultOverviewCustomer();
			} catch (error) {
				this.overviewError = error.message;
			}

			this.overviewLoadingTree = false;
		},

		async fetchOverview() {
			if (!this.selectedOverviewCustomer) return;

			this.overviewError = '';
			this.overviewLoading = true;
			this.overviewRows = [];
			this.expandedOverviewRowIds = [];
			this.selectedOverviewRow = null;
			this.selectedOverviewBuilding = null;
			this.selectedOverviewInventory = [];
			this.selectedOverviewInventoryItem = null;
			this.expandedOverviewInventoryGroupIds = [];
			this.resetDetailPanelExpansion('overview');
			this.resetSelectedMapPreview('overview');

			try {
				const selectedNode = this.findCustomerById(this.customerTree, this.selectedOverviewCustomer.id) || this.selectedOverviewCustomer;
				const customers = this.flattenCustomers([selectedNode]);
				let buildings = await this.fetchBuildingsForCustomers(customers);

				if (this.selectedOverviewBuildingFilterId) {
					buildings = buildings.filter(building => building.id === this.selectedOverviewBuildingFilterId);
				}

				const buildingRows = await Promise.all(buildings.map(building =>
					this.buildOverviewBuildingRow(building, 0, this.overviewStart, this.overviewStop)
				));

				if (this.selectedOverviewBuildingFilterId) {
					this.overviewRows = buildingRows
						.map(row => ({ ...row, level: 0 }))
						.sort(this.sortOverviewRows);
				} else {
					this.overviewRows = this.buildOverviewLevelRows(
						selectedNode,
						this.groupBuildingRowsByCustomer(buildingRows),
						0
					);
				}

				this.sortOverviewTree();
			} catch (error) {
				this.overviewError = error.message;
			}

			this.overviewLoading = false;
		},

		async fetchOverviewBuildingOptions() {
			if (!this.selectedOverviewCustomer) return;

			this.overviewLoadingBuildings = true;

			try {
				const selectedNode = this.findCustomerById(this.customerTree, this.selectedOverviewCustomer.id) || this.selectedOverviewCustomer;
				const customers = this.flattenCustomers([selectedNode]);
				this.overviewBuildingOptions = (await this.fetchBuildingsForCustomers(customers))
					.sort((a, b) => a.name.localeCompare(b.name));
			} catch (error) {
				this.overviewError = error.message;
			}

			this.overviewLoadingBuildings = false;
		},

		async fetchCompareOverview() {
			if (!this.compareSelectedCustomer) return;

			this.compareError = '';
			this.compareLoading = true;
			this.compareRows = [];
			this.compareExpandedRowIds = [];
			this.compareSelectedRow = null;
			this.compareSelectedInventory = [];
			this.compareSelectedInventoryItem = null;
			this.compareExpandedInventoryGroupIds = [];
			this.resetDetailPanelExpansion('compare');
			this.resetSelectedMapPreview('compare');

			try {
				const selectedNode = this.findCustomerById(this.customerTree, this.compareSelectedCustomer.id) || this.compareSelectedCustomer;
				const customers = this.flattenCustomers([selectedNode]);
				let buildings = await this.fetchBuildingsForCustomers(customers);

				if (this.compareBuildingFilterId) {
					buildings = buildings.filter(building => building.id === this.compareBuildingFilterId);
				}

				const buildingRows = await Promise.all(buildings.map(building =>
					this.buildOverviewBuildingRow(building, 0, this.compareStart, this.compareStop)
				));

				if (this.compareBuildingFilterId) {
					this.compareRows = buildingRows.map(row => ({ ...row, level: 0 }));
				} else {
					this.compareRows = this.buildOverviewLevelRows(
						selectedNode,
						this.groupBuildingRowsByCustomer(buildingRows),
						0
					);
				}

				this.sortCompareTree();
			} catch (error) {
				this.compareError = error.message;
			}

			this.compareLoading = false;
		},

		async fetchCompareBuildingOptions() {
			if (!this.compareSelectedCustomer) return;

			this.compareLoadingBuildings = true;

			try {
				const selectedNode = this.findCustomerById(this.customerTree, this.compareSelectedCustomer.id) || this.compareSelectedCustomer;
				const customers = this.flattenCustomers([selectedNode]);
				this.compareBuildingOptions = (await this.fetchBuildingsForCustomers(customers))
					.sort((a, b) => a.name.localeCompare(b.name));
			} catch (error) {
				this.compareError = error.message;
			}

			this.compareLoadingBuildings = false;
		},

		async fetchBuildingsForCustomers(customers) {
			const buildingGroups = await Promise.all(customers.map(async customer => {
				const buildings = await this.$services.building.getBuildingsOnCustomer(customer.id);

				return (Array.isArray(buildings) ? buildings : [])
					.map(building => ({
						...building,
						statisticsCustomerId: building.customerId || customer.id
					}));
			}));

			return this.uniqueBuildingsByLast(
				buildingGroups.reduce((acc, group) => [...acc, ...(Array.isArray(group) ? group : [])], [])
			);
		},

		async buildOverviewBuildingRow(building, level, start, stop) {
			const [inventory, flaws] = await Promise.all([
				this.fetchBuildingInventory(building),
				this.fetchBuildingFlaws(building, start, stop)
			]);

			const inventoryWithFlaws = this.mapInventoryFlaws(inventory, flaws)
				.map(item => ({
					...item,
					buildingId: building.id,
					buildingName: building.name
				}));
			const looseFlawItems = this.getLooseFlawInventoryItems(inventory, flaws, building);
			const flawCount = flaws.length;
			const inventoryCount = inventory.length;
			const handledFlawCount = flaws.filter(flaw => flaw.done).length;
			const unhandledFlawCount = flaws.filter(flaw => !flaw.done).length;

			return {
				id: `building-${building.id}`,
				type: 'building',
				name: building.name,
				building,
				level,
				children: [],
				inventoryCount,
				flawCount,
				handledFlawCount,
				unhandledFlawCount,
				totalCount: handledFlawCount + unhandledFlawCount,
				inventory: [...inventoryWithFlaws, ...looseFlawItems]
			};
		},

		buildOverviewDepartmentRow(customer, buildingRowsByCustomer, level) {
			const children = this.buildOverviewLevelRows(customer, buildingRowsByCustomer, level + 1);
			const totals = this.sumOverviewRows(children);
			const inventory = this.getOverviewInventoryFromRows(children);

			return {
				id: `customer-${customer.id}`,
				type: 'department',
				name: customer.name,
				customer,
				level,
				children,
				inventoryCount: totals.inventoryCount,
				flawCount: totals.flawCount,
				handledFlawCount: totals.handledFlawCount,
				unhandledFlawCount: totals.unhandledFlawCount,
				totalCount: totals.totalCount,
				inventory
			};
		},

		buildOverviewLevelRows(customer, buildingRowsByCustomer, level) {
			const departmentRows = (customer.children || [])
				.map(child => this.buildOverviewDepartmentRow(child, buildingRowsByCustomer, level));
			const buildingRows = (buildingRowsByCustomer[customer.id] || [])
				.map(row => ({ ...row, level }));

			return [...departmentRows, ...buildingRows]
				.filter(row => row.type === 'building' || row.totalCount > 0 || row.children.length > 0)
				.sort(this.sortOverviewRows);
		},

		groupBuildingRowsByCustomer(buildingRows) {
			return buildingRows.reduce((acc, row) => {
				const customerId = row.building.statisticsCustomerId || row.building.customerId || this.selectedOverviewCustomer?.id;
				if (!customerId) return acc;

				if (!acc[customerId]) acc[customerId] = [];
				acc[customerId].push(row);

				return acc;
			}, {});
		},

		sumOverviewRows(rows) {
			return (rows || []).reduce((acc, row) => ({
				inventoryCount: acc.inventoryCount + row.inventoryCount,
				flawCount: acc.flawCount + row.flawCount,
				handledFlawCount: acc.handledFlawCount + row.handledFlawCount,
				unhandledFlawCount: acc.unhandledFlawCount + row.unhandledFlawCount,
				totalCount: acc.totalCount + row.totalCount
			}), {
				inventoryCount: 0,
				flawCount: 0,
				handledFlawCount: 0,
				unhandledFlawCount: 0,
				totalCount: 0
			});
		},

		sortOverviewRows(a, b) {
			const modifier = this.overviewSortDirection === 'desc' ? -1 : 1;
			const valueA = this.getOverviewSortValue(a);
			const valueB = this.getOverviewSortValue(b);

			if (typeof valueA === 'string' || typeof valueB === 'string') {
				return String(valueA).localeCompare(String(valueB)) * modifier;
			}

			if (valueA !== valueB) return (valueA - valueB) * modifier;

			return a.name.localeCompare(b.name);
		},

		getOverviewSortValue(row) {
			const total = row.handledFlawCount + row.unhandledFlawCount;

			switch (this.overviewSortBy) {
				case 'handledFlaws':
					return row.handledFlawCount;
				case 'unhandledFlawPercent':
					return total ? row.unhandledFlawCount / total : 0;
				case 'handledFlawPercent':
					return total ? row.handledFlawCount / total : 0;
				case 'name':
					return row.name || '';
				case 'unhandledFlaws':
				default:
					return row.unhandledFlawCount;
			}
		},

		sortOverviewTree() {
			this.overviewRows = this.sortOverviewRowsRecursively(this.overviewRows);
		},

		sortOverviewRowsRecursively(rows) {
			return [...(rows || [])]
				.map(row => ({
					...row,
					children: this.sortOverviewRowsRecursively(row.children || [])
				}))
				.sort(this.sortOverviewRows);
		},

		sortCompareRows(a, b) {
			const modifier = this.compareSortDirection === 'desc' ? -1 : 1;
			const valueA = this.getCompareSortValue(a);
			const valueB = this.getCompareSortValue(b);

			if (typeof valueA === 'string' || typeof valueB === 'string') {
				return String(valueA).localeCompare(String(valueB)) * modifier;
			}

			if (valueA !== valueB) return (valueA - valueB) * modifier;

			return a.name.localeCompare(b.name);
		},

		getCompareSortValue(row) {
			const total = row.handledFlawCount + row.unhandledFlawCount;

			switch (this.compareSortBy) {
				case 'handledFlaws':
					return row.handledFlawCount;
				case 'unhandledFlawPercent':
					return total ? row.unhandledFlawCount / total : 0;
				case 'handledFlawPercent':
					return total ? row.handledFlawCount / total : 0;
				case 'name':
					return row.name || '';
				case 'unhandledFlaws':
				default:
					return row.unhandledFlawCount;
			}
		},

		sortCompareTree() {
			this.compareRows = this.sortCompareRowsRecursively(this.compareRows);
		},

		sortCompareRowsRecursively(rows) {
			return [...(rows || [])]
				.map(row => ({
					...row,
					children: this.sortCompareRowsRecursively(row.children || [])
				}))
				.sort(this.sortCompareRows);
		},

		async fetchBuildingInventory(building) {
			const floors = await this.$services.floor.getFloors({
				buildingId: building.id,
				orderBy: 'Name',
				currentPage: 1,
				pageSize: 1000
			});

			const mapsByFloor = await Promise.all((Array.isArray(floors) ? floors : []).map(async floor => {
				const maps = await this.$services.map.getMaps({
					floorId: floor.id,
					currentPage: 1,
					pageSize: 1000
				});

				return (Array.isArray(maps) ? maps : [])
					.map(map => ({ ...map, statisticsFloorName: floor.name || floor.floorName }));
			}));

			const maps = mapsByFloor.reduce((acc, group) => [...acc, ...group], []);
			const itemGroups = await Promise.all(maps.map(async map => {
				const items = await this.$services.mapItem.getMapItems({
					mapId: map.id,
					orderBy: 'PositionNumber',
					currentPage: 1,
					pageSize: 1000
				});

				return (Array.isArray(items) ? items : [])
					.map(item => this.normalizeInventoryInstance(item, map, building))
					.filter(Boolean);
			}));

			return itemGroups.reduce((acc, group) => [...acc, ...group], []);
		},

		async fetchBuildingFlaws(building, start, stop) {
			const data = await this.$services.flaw.getFlaws({
				customerId: building.statisticsCustomerId || building.customerId || this.selectedOverviewCustomer?.id,
				userBuildingId: this.getUserBuildingId,
				buildingId: building.id,
				start,
				stop,
				currentPage: 1,
				pageSize: 0
			});

			return Array.isArray(data) ? data : [];
		},

		mapInventoryFlaws(inventory, flaws) {
			const flawsByInventory = flaws.reduce((acc, flaw) => {
				const id = this.getFlawInventoryId(flaw);
				if (!id) return acc;

				if (!acc[id]) acc[id] = [];
				acc[id].push(flaw);

				return acc;
			}, {});

			return inventory
				.map(item => ({
					...item,
					flaws: flawsByInventory[item.id] || [],
					flawCount: (flawsByInventory[item.id] || []).length
				}))
				.sort((a, b) => b.flawCount - a.flawCount || this.getInventoryName(a).localeCompare(this.getInventoryName(b)));
		},

		getLooseFlawInventoryItems(inventory, flaws, building) {
			const inventoryIds = new Set((inventory || [])
				.map(item => String(item.id))
				.filter(Boolean));

			return (flaws || [])
				.filter(flaw => {
					const inventoryId = this.getFlawInventoryId(flaw);

					return !inventoryId || !inventoryIds.has(String(inventoryId));
				})
				.map((flaw, index) => ({
					id: `loose-flaw-${flaw.id || index}`,
					isLooseFlaw: true,
					name: this.getFlawName(flaw),
					inventoryType: 'loose-flaws',
					buildingId: building.id,
					buildingName: building.name,
					flaws: [flaw],
					flawCount: 1
				}));
		},

		normalizeInventoryInstance(item, map, building) {
			const fireProtection = item.fireProtection || item.mapItem?.fireProtection || {};
			const inventoryType = item.inventoryType || fireProtection.inventoryType;

			if (!item?.id || !inventoryType) return null;

			return {
				...item,
				id: item.id,
				mapItemId: item.id,
				name: item.name || fireProtection.name || item.mapItemName,
				positionNumber: item.positionNumber,
				inventoryType,
				fireProtection,
				floorName: map.statisticsFloorName,
				mapId: map.id,
				mapName: map.name,
				mapFileId: map.file?.id,
				map,
				buildingId: building.id,
				buildingName: building.name
			};
		},

		getFlawInventoryId(flaw) {
			return flaw.mapItemId
				|| flaw.mapItem?.id
				|| flaw.roundHistoryItem?.mapItemId
				|| flaw.roundHistoryItem?.mapItem?.id
				|| flaw.fireProtectionId
				|| flaw.fireProtection?.id
				|| flaw.mapItem?.fireProtectionId
				|| flaw.mapItem?.fireProtection?.id
				|| flaw.roundHistoryItem?.fireProtectionId
				|| flaw.roundHistoryItem?.fireProtection?.id;
		},

		toggleOverviewRow(row) {
			if (!row.children.length) return;

			if (this.expandedOverviewRowIds.includes(row.id)) {
				this.expandedOverviewRowIds = this.expandedOverviewRowIds.filter(id => id !== row.id);

				return;
			}

			this.expandedOverviewRowIds = [...this.expandedOverviewRowIds, row.id];
		},

		isOverviewRowExpanded(row) {
			return this.expandedOverviewRowIds.includes(row.id);
		},

		selectOverviewRow(row) {
			if (this.selectedOverviewRow && this.selectedOverviewRow.id === row.id) {
				this.selectedOverviewRow = null;
				this.selectedOverviewBuilding = null;
				this.selectedOverviewInventory = [];
				this.selectedOverviewInventoryItem = null;
				this.expandedOverviewInventoryGroupIds = [];
				this.resetDetailPanelExpansion('overview');
				this.resetSelectedMapPreview('overview');

				return;
			}

			this.selectedOverviewRow = row;
			this.selectedOverviewBuilding = row.type === 'building' ? row.building : null;
			this.selectedOverviewInventory = this.sortOverviewInventory(row.inventory || []);
			this.selectedOverviewInventoryItem = null;
			this.expandedOverviewInventoryGroupIds = [];
			this.resetDetailPanelExpansion('overview');
			this.resetSelectedMapPreview('overview');
		},

		selectOverviewInventoryItem(item) {
			this.selectedOverviewInventoryItem = item;
			this.closeInventoryMapModal();
		},

		toggleCompareRow(row) {
			if (!row.children.length) return;

			if (this.compareExpandedRowIds.includes(row.id)) {
				this.compareExpandedRowIds = this.compareExpandedRowIds.filter(id => id !== row.id);

				return;
			}

			this.compareExpandedRowIds = [...this.compareExpandedRowIds, row.id];
		},

		isCompareRowExpanded(row) {
			return this.compareExpandedRowIds.includes(row.id);
		},

		selectCompareRow(row) {
			if (this.compareSelectedRow && this.compareSelectedRow.id === row.id) {
				this.compareSelectedRow = null;
				this.compareSelectedInventory = [];
				this.compareSelectedInventoryItem = null;
				this.compareExpandedInventoryGroupIds = [];
				this.resetDetailPanelExpansion('compare');
				this.resetSelectedMapPreview('compare');

				return;
			}

			this.compareSelectedRow = row;
			this.compareSelectedInventory = this.sortOverviewInventory(row.inventory || []);
			this.compareSelectedInventoryItem = null;
			this.compareExpandedInventoryGroupIds = [];
			this.resetDetailPanelExpansion('compare');
			this.resetSelectedMapPreview('compare');
		},

		selectCompareInventoryItem(item) {
			this.compareSelectedInventoryItem = item;
			this.closeInventoryMapModal();
		},

		openInventoryMapModal(item, target) {
			this.mapModalTarget = target;
			this.mapModalOpen = true;
			this.loadSelectedInventoryMap(item, target);
		},

		closeInventoryMapModal() {
			this.mapModalOpen = false;
		},

		getMapModalItem() {
			return this.mapModalTarget === 'compare'
				? this.compareSelectedInventoryItem
				: this.selectedOverviewInventoryItem;
		},

		getMapModalImage() {
			return this.mapModalTarget === 'compare'
				? this.compareSelectedMapImage
				: this.selectedOverviewMapImage;
		},

		getMapModalLoading() {
			return this.mapModalTarget === 'compare'
				? this.compareSelectedMapLoading
				: this.selectedOverviewMapLoading;
		},

		getMapModalError() {
			return this.mapModalTarget === 'compare'
				? this.compareSelectedMapError
				: this.selectedOverviewMapError;
		},

		getMapModalTitle() {
			const item = this.getMapModalItem();
			if (!item) return this.$t('statistics.drawing');

			return this.getInventoryMapTitle(item);
		},

		resetSelectedMapPreview(target) {
			if (target === 'compare') {
				this.compareSelectedMapImage = '';
				this.compareSelectedMapLoading = false;
				this.compareSelectedMapError = '';

				return;
			}

			this.selectedOverviewMapImage = '';
			this.selectedOverviewMapLoading = false;
			this.selectedOverviewMapError = '';
		},

		async loadSelectedInventoryMap(item, target) {
			this.resetSelectedMapPreview(target);

			if (item?.isLooseFlaw) {
				if (target === 'compare') {
					this.compareSelectedMapError = this.$t('statistics.loose_flaw_missing_inventory_link');
				} else {
					this.selectedOverviewMapError = this.$t('statistics.loose_flaw_missing_inventory_link');
				}

				return;
			}

			const fileId = this.getInventoryMapFileId(item);
			if (!fileId) {
				if (target === 'compare') {
					this.compareSelectedMapError = this.$t('statistics.drawing_missing_for_inventory');
				} else {
					this.selectedOverviewMapError = this.$t('statistics.drawing_missing_for_inventory');
				}

				return;
			}

			if (target === 'compare') {
				this.compareSelectedMapLoading = true;
			} else {
				this.selectedOverviewMapLoading = true;
			}

			try {
				const image = this.mapImageCache[fileId] || await this.getFile(fileId);
				if (image) this.$set(this.mapImageCache, fileId, image);

				const stillSelected = target === 'compare'
					? this.compareSelectedInventoryItem?.id === item.id
					: this.selectedOverviewInventoryItem?.id === item.id;

				if (!stillSelected) return;

				if (target === 'compare') {
					this.compareSelectedMapImage = image || '';
					this.compareSelectedMapError = image ? '' : this.$t('statistics.drawing_fetch_failed');
				} else {
					this.selectedOverviewMapImage = image || '';
					this.selectedOverviewMapError = image ? '' : this.$t('statistics.drawing_fetch_failed');
				}
			} catch (error) {
				if (target === 'compare') {
					this.compareSelectedMapError = error.message || this.$t('statistics.drawing_fetch_failed');
				} else {
					this.selectedOverviewMapError = error.message || this.$t('statistics.drawing_fetch_failed');
				}
			} finally {
				if (target === 'compare') {
					this.compareSelectedMapLoading = false;
				} else {
					this.selectedOverviewMapLoading = false;
				}
			}
		},

		getInventoryMapFileId(item) {
			return item?.mapFileId || item?.map?.file?.id || item?.fileId;
		},

		getInventoryMapTitle(item) {
			return [item?.buildingName, item?.floorName, item?.mapName]
				.filter(Boolean)
				.join(' - ') || this.$t('statistics.drawing');
		},

		hasInventoryMapPosition(item) {
			return item?.positionX !== null
				&& item?.positionX !== undefined
				&& item?.positionY !== null
				&& item?.positionY !== undefined;
		},

		getInventoryMapMarkerStyle(item) {
			const x = Math.max(0, Math.min(100, Number(item?.positionX || 0) / 10));
			const y = Math.max(0, Math.min(100, Number(item?.positionY || 0) / 10));

			return {
				left: `${x}%`,
				bottom: `${y}%`
			};
		},

		getOverviewSegmentWidth(value, segmentType = '') {
			if (!this.maxOverviewCount) return '0%';
			if ((segmentType === 'handled' || segmentType === 'unhandled') && value > 0) {
				return `max(${(value / this.maxOverviewCount) * 100}%, 34px)`;
			}

			return `${(value / this.maxOverviewCount) * 100}%`;
		},

		getOverviewHandledFlawLabel(row) {
			if (this.overviewDisplayMode === 'percent') {
				return `${this.getOverviewHandledFlawPercent(row)}%`;
			}

			return row.handledFlawCount;
		},

		getOverviewUnhandledFlawLabel(row) {
			if (this.overviewDisplayMode === 'percent') {
				return `${this.getOverviewUnhandledFlawPercent(row)}%`;
			}

			return row.unhandledFlawCount;
		},

		getOverviewHandledFlawPercent(row) {
			const total = row.handledFlawCount + row.unhandledFlawCount;
			if (!total) return 0;

			return Math.round((row.handledFlawCount / total) * 100);
		},

		getOverviewUnhandledFlawPercent(row) {
			const total = row.handledFlawCount + row.unhandledFlawCount;
			if (!total) return 0;

			return Math.round((row.unhandledFlawCount / total) * 100);
		},

		getCompareSegmentWidth(value, segmentType = '') {
			if (!this.maxCompareCount) return '0%';
			if ((segmentType === 'handled' || segmentType === 'unhandled') && value > 0) {
				return `max(${(value / this.maxCompareCount) * 100}%, 34px)`;
			}

			return `${(value / this.maxCompareCount) * 100}%`;
		},

		getCompareHandledFlawLabel(row) {
			if (this.compareDisplayMode === 'percent') {
				return `${this.getOverviewHandledFlawPercent(row)}%`;
			}

			return row.handledFlawCount;
		},

		getCompareUnhandledFlawLabel(row) {
			if (this.compareDisplayMode === 'percent') {
				return `${this.getOverviewUnhandledFlawPercent(row)}%`;
			}

			return row.unhandledFlawCount;
		},

		getOverviewTrackHeight(row) {
			return `${Math.max(24, 32 - (row.level * 3))}px`;
		},

		getOverviewRowHeight(row) {
			return `${Math.max(42, 52 - (row.level * 4))}px`;
		},

		getInventoryName(item) {
			if (item?.isLooseFlaw) {
				return item.name || this.$t('statistics.loose_flaw');
			}

			return this.translateInventoryLabel(item.name)
				|| item.positionNumber
				|| this.displayType(item)
				|| item.inventoryType
				|| this.$t('statistics.inventory');
		},

		translateInventoryLabel(label) {
			if (!label) return '';
			if (typeof label !== 'string') return label;

			return this.tTypeKey(label);
		},

		getInventoryTypeLabel(item) {
			if (item?.isLooseFlaw) return this.$t('statistics.loose_flaws');

			return this.displayType(item)
				|| this.translateInventoryLabel(item.inventoryType)
				|| this.$t('statistics.other_inventories');
		},

		getInventoryTypeGroupId(item) {
			if (item?.isLooseFlaw) return 'loose-flaws';

			const type = item?.inventoryType || item?.fireProtection?.inventoryType || this.getInventoryTypeLabel(item);
			const normalized = this.normalizeTypeBare(type) || String(type || '').trim().toLowerCase();

			return normalized || 'other';
		},

		getInventoryMapIconClass(item) {
			const type = item?.fireProtection?.inventoryType
				|| item?.inventoryType
				|| item?.mapItem?.fireProtection?.inventoryType
				|| item?.flaws?.[0]?.mapItem?.fireProtection?.inventoryType
				|| item?.flaws?.[0]?.inventoryType
				|| 'other';

			return this.getType(type)?.icon || this.getType('other')?.icon || 'misc';
		},

		getGroupedInventory(inventory) {
			const groupsById = {};

			(inventory || []).forEach(item => {
				const id = this.getInventoryTypeGroupId(item);

				if (!groupsById[id]) {
					groupsById[id] = {
						id,
						label: this.getInventoryTypeLabel(item),
						items: [],
						flawCount: 0
					};
				}

				groupsById[id].items.push(item);
				groupsById[id].flawCount += item.flawCount || 0;
			});

			return Object.values(groupsById)
				.map(group => ({
					...group,
					count: group.items.length,
					items: this.sortOverviewInventory(group.items)
				}))
				.sort((a, b) => b.flawCount - a.flawCount
					|| b.count - a.count
					|| a.label.localeCompare(b.label));
		},

		getVisibleInventoryRows(inventory, target) {
			return this.getGroupedInventory(inventory).reduce((rows, group) => {
				const groupRow = {
					type: 'group',
					key: `${target}-group-${group.id}`,
					...group
				};

				if (!this.isInventoryGroupExpanded(group.id, target)) {
					return [...rows, groupRow];
				}

				const itemRows = group.items.map(item => ({
					type: 'item',
					key: `${target}-item-${item.buildingId || 'building'}-${item.id || item.name}`,
					item
				}));

				return [...rows, groupRow, ...itemRows];
			}, []);
		},

		getExpandedInventoryGroupIds(target) {
			return target === 'compare'
				? this.compareExpandedInventoryGroupIds
				: this.expandedOverviewInventoryGroupIds;
		},

		setExpandedInventoryGroupIds(target, ids) {
			if (target === 'compare') {
				this.compareExpandedInventoryGroupIds = ids;

				return;
			}

			this.expandedOverviewInventoryGroupIds = ids;
		},

		isInventoryGroupExpanded(groupId, target) {
			return this.getExpandedInventoryGroupIds(target).includes(groupId);
		},

		isOverviewInventoryGroupExpanded(groupId) {
			return this.isInventoryGroupExpanded(groupId, 'overview');
		},

		isCompareInventoryGroupExpanded(groupId) {
			return this.isInventoryGroupExpanded(groupId, 'compare');
		},

		areAllInventoryGroupsExpanded(target, inventory) {
			const groupIds = this.getInventoryGroupIds(inventory);
			const expandedIds = this.getExpandedInventoryGroupIds(target);

			return !!groupIds.length && groupIds.every(id => expandedIds.includes(id));
		},

		toggleAllInventoryGroups(target, inventory) {
			if (this.areAllInventoryGroupsExpanded(target, inventory)) {
				this.setExpandedInventoryGroupIds(target, []);

				return;
			}

			this.setExpandedInventoryGroupIds(target, this.getInventoryGroupIds(inventory));
		},

		toggleInventoryGroup(groupId, target) {
			const expandedIds = this.getExpandedInventoryGroupIds(target);

			if (expandedIds.includes(groupId)) {
				this.setExpandedInventoryGroupIds(target, expandedIds.filter(id => id !== groupId));

				return;
			}

			this.setExpandedInventoryGroupIds(target, [...expandedIds, groupId]);
		},

		handleInventoryRowClick(row, target) {
			if (row.type === 'group') {
				this.toggleInventoryGroup(row.id, target);

				return;
			}

			if (target === 'compare') {
				this.selectCompareInventoryItem(row.item);

				return;
			}

			this.selectOverviewInventoryItem(row.item);
		},

		getExpandedDetailPanels(target) {
			return target === 'compare'
				? this.compareExpandedDetailPanels
				: this.expandedOverviewDetailPanels;
		},

		isDetailPanelExpanded(target, panel) {
			return !!this.getExpandedDetailPanels(target)?.[panel];
		},

		setDetailPanelExpanded(target, panel, expanded) {
			const panels = {
				...this.getExpandedDetailPanels(target),
				[panel]: expanded
			};

			if (target === 'compare') {
				this.compareExpandedDetailPanels = panels;

				return;
			}

			this.expandedOverviewDetailPanels = panels;
		},

		toggleDetailPanelExpansion(target, panel) {
			this.setDetailPanelExpanded(target, panel, !this.isDetailPanelExpanded(target, panel));
		},

		resetDetailPanelExpansion(target) {
			if (target === 'compare') {
				this.compareExpandedDetailPanels = {
					inventory: false,
					flaws: false
				};

				return;
			}

			this.expandedOverviewDetailPanels = {
				inventory: false,
				flaws: false
			};
		},

		getDetailPanelClass(target, panel) {
			return {
				'statistics-detail-panel--expanded': this.isDetailPanelExpanded(target, panel)
			};
		},

		getDetailLayoutClass(target) {
			const panels = this.getExpandedDetailPanels(target);

			return {
				'statistics-detail-layout--has-expanded-panel': !!(panels?.inventory || panels?.flaws)
			};
		},

		getDetailPanelExpandTitle(target, panel) {
			return this.isDetailPanelExpanded(target, panel)
				? this.$t('statistics.minimize_panel')
				: this.$t('statistics.expand_panel');
		},

		getFlawName(flaw) {
			return flaw.name || flaw.description || flaw.flawType?.name || this.$t('statistics.flaw');
		},

		getOverviewInventoryFromRows(rows) {
			return this.sortOverviewInventory((rows || []).reduce((acc, row) => {
				if (row.type === 'building') {
					return [...acc, ...(row.inventory || [])];
				}

				return [...acc, ...this.getOverviewInventoryFromRows(row.children || [])];
			}, []));
		},

		sortOverviewInventory(inventory) {
			return [...(inventory || [])]
				.sort((a, b) => b.flawCount - a.flawCount
					|| String(a.buildingName || '').localeCompare(String(b.buildingName || ''))
					|| this.getInventoryName(a).localeCompare(this.getInventoryName(b)));
		},

		findCustomerById(items, id) {
			for (const item of items || []) {
				if (item.id === id) return item;

				const child = this.findCustomerById(item.children || [], id);
				if (child) return child;
			}

			return null;
		},

		flattenCustomers(items) {
			return (items || []).reduce((acc, item) => [
				...acc,
				item,
				...this.flattenCustomers(item.children || [])
			], []);
		},

		flattenOverviewRows(rows) {
			return (rows || []).reduce((acc, row) => [
				...acc,
				row,
				...this.flattenOverviewRows(row.children || [])
			], []);
		},

		flattenVisibleOverviewRows(rows) {
			return (rows || []).reduce((acc, row) => {
				const children = this.isOverviewRowExpanded(row)
					? this.flattenVisibleOverviewRows(row.children || [])
					: [];

				return [...acc, row, ...children];
			}, []);
		},

		flattenVisibleCompareRows(rows) {
			return (rows || []).reduce((acc, row) => {
				const children = this.isCompareRowExpanded(row)
					? this.flattenVisibleCompareRows(row.children || [])
					: [];

				return [...acc, row, ...children];
			}, []);
		},

		flattenStructureOptions(items, level = 0) {
			return (items || []).reduce((acc, item) => [
				...acc,
				{ ...item, level, children: item.children || [] },
				...this.flattenStructureOptions(item.children || [], level + 1)
			], []);
		},

		getVisibleStructureOptions(items, level = 0) {
			return (items || []).reduce((acc, item) => {
				const option = { ...item, level, children: item.children || [] };
				const children = this.isStructureExpanded(item.id)
					? this.getVisibleStructureOptions(item.children || [], level + 1)
					: [];

				return [...acc, option, ...children];
			}, []);
		},

		getVisibleCompareStructureOptions(items, level = 0) {
			return (items || []).reduce((acc, item) => {
				const option = { ...item, level, children: item.children || [] };
				const children = this.isCompareStructureExpanded(item.id)
					? this.getVisibleCompareStructureOptions(item.children || [], level + 1)
					: [];

				return [...acc, option, ...children];
			}, []);
		},

		uniqueBy(items, key) {
			const seen = {};

			return (items || []).filter(item => {
				const value = item && item[key];
				if (!value || seen[value]) return false;

				seen[value] = true;

				return true;
			});
		},

		uniqueBuildingsByLast(buildings) {
			const byId = {};

			(buildings || []).forEach(building => {
				if (!building?.id) return;
				byId[building.id] = building;
			});

			return Object.values(byId);
		},

		selectTab(index) {
			this.selectedTab = index;
		},

		getColor(roundHistory) {
			const colorMap = {
				0: 'green',
				1: 'red',
				2: 'yellow',
				3: 'grey',
				4: 'orange'
			};
			const colorIndex = roundHistory.started && roundHistory.expired
				? 4
				: roundHistory.done && !roundHistory.expired
					? 0
					: roundHistory.started
						? 2
						: !roundHistory.created || !roundHistory.done
							? 3
							: 1;

			return colorMap[colorIndex];
		},

		setCurrentYear(year) {
			this.year = year;

			this.refreshData();
		},

		async getStatistics(buildingId, year) {
			this.flawStatistics = {};
			this.roundPlanningStatistics = [];
			this.roundCount = [0, 0, 0, 0];

			await this.getFlawStatistic(buildingId, year);
			await this.getRoundStatistics(buildingId, year);
		},

		async getFlawStatistic(buildingId, year) {
			const data = await this.$services.flaw.getFlawStatistic({
				customerId: this.getActiveCustomer?.id,
				userBuildingId: this.getUserBuildingId,
				buildingId: buildingId,
				Start: `${year}-01-01`,
				Stop: `${year}-12-31`,
				pageSize: 0
			});

			this.flawStatistics = data;
		},

		async getRoundStatistics(buildingId, year) {
			const data = await this.$services.roundPlanning.getRoundStatistics({
				customerId: this.getActiveCustomer?.id,
				buildingId: buildingId,
				Start: `${year}-01-01`,
				Stop: `${year}-12-31`,
				pageSize: 0,
				orderBy: 'Name'
			});

			this.roundPlanningStatistics = data;
		},

		getRoundHistoryCount() {
			const currentMonth = this.moment(new Date()).format('M');

			if (this.roundPlanningStatistics.length  < 1) return;

			this.roundCount = this.roundPlanningStatistics.reduce((roundAcc, round) => {
				let roundCount = round.roundHistoryStatistics.reduce((acc, history) => {
					const roundHistoryMonth = history.created ? this.moment(history.created).format('M') : -1;

					if (history.done && !history.expired) acc[0] = acc[0] + 1;

					if (!history.done && history.started) acc[1] = acc[1] + 1;

					if (history.done && currentMonth !== roundHistoryMonth && roundHistoryMonth !== -1) acc[2] = acc[2] + 1;

					if (history.started && history.expired) acc[3] = acc[3] + 1;

					return acc;
				},[ 0, 0, 0, 0 ]);

				roundAcc[0] = roundAcc[0] + roundCount[0];
				roundAcc[1] = roundAcc[1] + roundCount[1];
				roundAcc[2] = roundAcc[2] + roundCount[2];
				roundAcc[3] = roundAcc[3] + roundCount[3];

				return roundAcc;
			}, [ 0, 0, 0, 0 ]);
		},

		refreshData() {
			if (!this.getActiveCustomer?.id) return;

			this.getStatistics(this.selectedBuilding?.id || null, this.year);
		}
	},

	created() {
		this.year = this.moment().format('YYYY');
		this.overviewStart = this.moment().startOf('year').format('YYYY-MM-DD');
		this.overviewStop = this.moment().endOf('year').format('YYYY-MM-DD');
		this.compareStart = this.overviewStart;
		this.compareStop = this.overviewStop;
		const yearsSinceStart = this.moment().diff('2020-01-01', 'years');
		this.years = [];
		for(let i = 0; i <= yearsSinceStart; i++) {
			this.years.push(this.moment('2020-01-01').add(i, 'years').format('YYYY'));
		}
		this.years.reverse();

		this.refreshData();
		this.fetchOverviewTree();
		this.fetchLegacyBuildingOptions();
	},

	mounted() {
		window.addEventListener('beforeprint', this.prepareStatisticsPrint);
		window.addEventListener('afterprint', this.restoreStatisticsPrint);
	},

	beforeDestroy() {
		window.removeEventListener('beforeprint', this.prepareStatisticsPrint);
		window.removeEventListener('afterprint', this.restoreStatisticsPrint);
	}
};
</script>

<style lang="scss">
@import "@/assets/colors.scss";

.statistics-overview {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.legacy-building-select {
	margin: .75rem 0 1.5rem;
	width: 220px;
}

.statistics-compare-layout {
	display: grid;
	gap: 1rem;
	grid-template-columns: minmax(0, 1fr);

	&.has-overview-detail:not(.active) {
		grid-template-columns: minmax(0, 1.65fr) minmax(520px, 1fr);

		.statistics-overview-detail {
			align-self: start;
			grid-column: 2;
			grid-row: 1;
			grid-template-columns: minmax(0, 1fr);
		}
	}

	&.active {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

.statistics-search-column {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 0;
}

.statistics-filter-panel {
	align-items: flex-end;
	background: $light;
	border: 1px solid #4a4e58;
	border-radius: 3px;
	box-shadow: rgba(0, 0, 0, .25) 0 1px 2px, rgba(0, 0, 0, .2) 0 4px 12px, rgba(255, 255, 255, .08) 0 0 0 1px inset;
	color: white;
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	padding: 1rem;

	.label {
		color: white;
	}

	.input,
	.structure-select-trigger {
		background-color: white;
	}
}

.statistics-compare-button {
	align-items: center;
	display: flex;
	gap: .5rem;
	margin-left: auto;

	i {
		color: white;
	}
}

.statistics-print-button {
	align-items: center;
	display: flex;
	gap: .5rem;

	i {
		color: white;
	}
}

.statistics-filter {
	min-width: 240px;
}

.date-filter {
	min-width: 160px;
}

.building-filter {
	min-width: 220px;
}

.statistics-search-button {
	min-width: 140px;
}

.statistics-chart-panel,
.statistics-inventory-panel,
.statistics-flaw-panel {
	background: $light;
	border: 1px solid #4a4e58;
	border-radius: 3px;
	box-shadow: rgba(0, 0, 0, .25) 0 1px 2px, rgba(0, 0, 0, .2) 0 4px 12px, rgba(255, 255, 255, .08) 0 0 0 1px inset;
	color: white;
	padding: 1rem;
}

.statistics-detail-layout {
	display: grid;
	gap: 1rem;
	grid-template-columns: minmax(320px, 1fr) minmax(0, 2fr);
	min-height: 0;
}

.statistics-overview-detail,
.statistics-compare-layout.active .statistics-detail-layout {
	grid-template-rows: minmax(0, 2fr) minmax(0, 1fr);
	height: 82vh;
	min-height: 640px;
}

.statistics-detail-layout--has-expanded-panel {
	height: auto;
	grid-template-rows: none;
}

.statistics-detail-layout--has-expanded-panel .statistics-inventory-panel:not(.statistics-detail-panel--expanded) {
	max-height: 44vh;
}

.statistics-detail-layout--has-expanded-panel .statistics-flaw-panel:not(.statistics-detail-panel--expanded) {
	max-height: 32vh;
}

.statistics-inventory-panel,
.statistics-flaw-panel {
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	position: relative;
}

.statistics-flaw-panel {
	min-height: 260px;
}

.statistics-detail-panel--expanded {
	max-height: none;
	overflow: visible;
}

.statistics-panel-title {
	align-items: flex-start;
	display: flex;
	gap: .75rem;
	justify-content: space-between;
	margin-bottom: .75rem;
}

.statistics-panel-title .list-title {
	color: white;
	margin-bottom: 0;
}

.statistics-panel-expand-button {
	align-items: center;
	background: transparent;
	border: 1px solid transparent;
	border-radius: 4px;
	color: $light-grey;
	cursor: pointer;
	display: flex;
	flex: 0 0 28px;
	height: 28px;
	justify-content: center;
	width: 28px;

	&:hover {
		background: rgba(255, 255, 255, .08);
		border-color: $lighter-grey;
		color: white;
	}
}

.statistics-panel-action-button {
	align-items: center;
	background: transparent;
	border: 1px solid rgba(255, 255, 255, .18);
	border-radius: 3px;
	color: $white;
	cursor: pointer;
	display: flex;
	font-size: 12px;
	gap: .35rem;
	margin-left: auto;
	padding: .25rem .45rem;

	i {
		color: $info;
	}

	&:hover {
		background: rgba(255, 255, 255, .08);
		border-color: rgba(255, 255, 255, .32);
	}
}

.statistics-compare-layout.active {
	.statistics-overview-detail {
		grid-column: 1;
		grid-row: 2;
	}

	.statistics-detail-layout {
		grid-template-columns: minmax(0, 1fr);
	}

	.statistics-chart-header {
		align-items: flex-start;
		gap: .75rem;
	}

	.statistics-chart-title {
		flex: 0 0 100%;
	}
}

.statistics-chart-header {
	align-items: flex-start;
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 1rem;
}

.statistics-chart-title {
	min-width: 220px;

	.list-title {
		color: white;
	}
}

.statistics-muted {
	color: $light-grey;
	font-size: 13px;
}

.statistics-title-range {
	color: $light-grey;
	display: inline-block;
	font-size: 13px;
	font-weight: 400;
	margin-left: .5rem;
	white-space: nowrap;
}

.statistics-legend {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	gap: .75rem;
	font-size: 13px;
	min-height: 28px;
}

.statistics-chart-mode {
	border: 1px solid $lighter-grey;
	border-radius: 4px;
	display: flex;
	overflow: hidden;

	button {
		background: white;
		border: 0;
		color: $dark;
		cursor: pointer;
		font-size: 13px;
		height: 28px;
		min-width: 54px;
		padding: 0 10px;

		&:not(:last-child) {
			border-right: 1px solid $lighter-grey;
		}

		&.active {
			background: $lightest-grey;
			font-weight: bold;
		}
	}
}

.statistics-sort-control {
	align-items: center;
	display: flex;
	gap: .25rem;
	margin-left: auto;

	.sort-icon {
		width: 30px;

		i {
			color: $info;
			cursor: pointer;
			pointer-events: auto;
		}
	}
}

.statistics-sort-select {
	min-width: 170px;
}

.legend-box {
	display: inline-block;
	border-radius: 2px;
	height: 12px;
	margin-right: 4px;
	width: 18px;

	&.inventory {
		background: #7bca78;
	}

	&.handled {
		background: #7bca78;
	}

	&.flaws {
		background: #da6060;
	}

	&.unhandled {
		background: #da6060;
	}
}

.building-chart {
	background: transparent;
	border-left: 0;
	padding: .5rem 0 0;
}

.building-chart-row {
	align-items: center;
	border: 1px solid rgba(255, 255, 255, .08);
	border-radius: 4px;
	cursor: pointer;
	display: grid;
	gap: .85rem;
	grid-template-columns: minmax(180px, 260px) minmax(180px, 760px) minmax(0, 1fr);
	min-height: 52px;
	margin-bottom: .35rem;
	padding: .45rem .65rem;
	position: relative;
	transition: background-color .16s ease, border-color .16s ease, box-shadow .16s ease;

	&:nth-child(odd) {
		background: rgba(255, 255, 255, .025);
	}

	&:nth-child(even) {
		background: rgba(255, 255, 255, .045);
	}

	&:hover {
		background: rgba(255, 255, 255, .08);
		border-color: rgba(255, 255, 255, .16);
	}

	&.active {
		background: rgba(253, 126, 20, .14);
		border-color: rgba(253, 126, 20, .45);
		box-shadow: inset 4px 0 0 $info;

		.building-chart-label {
			color: white;
			font-weight: 700;
		}
	}

	&--department {
		.building-chart-label {
			font-weight: 600;
		}
	}

	&--child {
		background: rgba(255, 255, 255, .03);
		border-color: rgba(255, 255, 255, .06);
		margin-left: 1.2rem;

		&::before {
			background: rgba(253, 126, 20, .55);
			border-radius: 999px;
			content: '';
			height: calc(100% + .35rem);
			left: -.65rem;
			position: absolute;
			top: -.35rem;
			width: 2px;
		}

		&::after {
			background: rgba(253, 126, 20, .55);
			content: '';
			height: 2px;
			left: -.65rem;
			position: absolute;
			top: 50%;
			width: .55rem;
		}

		.building-chart-label {
			color: rgba(255, 255, 255, .9);
			font-weight: 500;
		}
	}

	&--nested {
		margin-left: 2.4rem;

		&::before,
		&::after {
			background: rgba(255, 255, 255, .24);
		}
	}
}

.building-chart-label {
	align-items: center;
	color: white;
	display: flex;
	font-size: 13px;
	min-width: 0;
	overflow: hidden;
	padding-left: calc(var(--row-level, 0) * 10px);
	text-overflow: ellipsis;
	white-space: nowrap;
}

.building-chart-expand,
.building-chart-expand-placeholder {
	align-items: center;
	border-radius: 4px;
	display: inline-flex;
	flex: 0 0 28px;
	height: 28px;
	justify-content: center;
	margin-right: 6px;
	width: 28px;
}

.building-chart-expand {
	background: transparent;
	border: 0;
	color: $info;
	cursor: pointer;
	font-size: 14px;
	padding: 0;
	transition: background-color .16s ease, color .16s ease;

	&:hover {
		background: rgba(255, 255, 255, .14);
		color: lighten($info, 10%);
	}

	&:focus {
		outline: none;
	}
}

.building-chart-track {
	background: rgba(255, 255, 255, .08);
	border: 1px solid rgba(255, 255, 255, .1);
	border-radius: 4px;
	display: flex;
	height: 32px;
	min-width: 180px;
	overflow: hidden;
	width: 100%;

	&:hover {
		border-color: rgba(255, 255, 255, .2);
		filter: brightness(1.03);
	}
}

.building-chart-segment {
	align-items: center;
	border: 0;
	color: white;
	display: flex;
	font-size: 12px;
	font-weight: bold;
	justify-content: center;
	min-width: 0;
	overflow: hidden;
	text-shadow: 0 1px 1px rgba(0, 0, 0, .2);

	&.inventory,
	&.handled {
		background: #7bca78;
		border-radius: 4px 0 0 4px;
	}

	&.flaws,
	&.unhandled {
		background: #da6060;
		border-radius: 0 4px 4px 0;
	}

	&.unhandled.is-first {
		border-radius: 4px 0 0 4px;
	}

	&.handled.is-last {
		border-radius: 0 4px 4px 0;
	}

	&.unhandled.is-first,
	&.handled.is-last {
		border-radius: 4px;
	}

}

.statistics-empty {
	color: $light-grey;
	font-size: 14px;
	padding: 1rem 0;

	&.compact {
		padding: .75rem;
	}
}

.statistics-inventory-header,
.statistics-inventory-row {
	align-items: center;
	display: grid;
	grid-template-columns: minmax(0, 50%) minmax(0, 25%) minmax(0, 12.5%) minmax(0, 12.5%);
	gap: .65rem;
	margin-left: 0;
	margin-right: 0;
	width: 100%;

	.panel-header-item,
	.panel-list-item {
		align-items: center;
		display: flex;
		max-width: none;
		min-width: 0;
		width: 100%;
	}
}

.statistics-inventory-row {
	cursor: pointer;
	min-height: 64px;

	&:hover,
	&.active {
		background: $list-background-hover;
		color: white;

		i {
			color: white;
		}
	}

	&.active {
		border-left: 3px solid $info;
	}

	&--group {
		background: $lightest-grey;
		color: $dark;
		font-weight: 700;
		grid-template-columns: minmax(0, 50%) minmax(0, 25%) minmax(0, 25%) !important;

		.inventory-name {
			grid-column: 1 !important;
		}

		.inventory-count {
			grid-column: 2 !important;
		}

		.narrow-count {
			grid-column: 3 !important;
		}
	}

	&--child {
		grid-template-columns: minmax(0, 50%) minmax(0, 40%) minmax(0, 10%);

		.inventory-name {
			padding-left: 1.25rem;
		}
	}
}

.inventory-name {
	gap: .5rem;
	justify-content: flex-start;
	line-height: 1.25;
	overflow: visible;
	text-align: left;
	white-space: normal;
	word-break: normal;
}

.inventory-name-text {
	min-width: 0;
	overflow-wrap: break-word;
	word-break: normal;
}

.inventory-name--group {
	font-weight: 700;
}

.statistics-inventory-group-icon {
	color: $info;
	flex: 0 0 12px;
	width: 12px;
}

.inventory-building {
	justify-content: flex-start;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.inventory-count {
	font-variant-numeric: tabular-nums;
	justify-content: flex-end;
	text-align: right;
}

.narrow-count {
	font-variant-numeric: tabular-nums;
	justify-content: flex-end;
	text-align: right;
}

.detail-subtitle {
	margin-bottom: .75rem;
}

.statistics-map-action {
	align-items: center;
	display: flex;
	gap: .75rem;
	margin-bottom: 1rem;

	.button {
		align-items: center;
		display: flex;
		gap: .4rem;

		i {
			color: white;
		}
	}
}

.statistics-flaw-panel .statistics-map-preview {
	display: none;
}

.statistics-map-preview {
	border: 1px solid $lighter-grey;
	border-radius: 6px;
	margin-bottom: 1rem;
	overflow: hidden;
}

.statistics-map-title {
	background: $lightest-grey;
	color: $dark;
	font-size: 13px;
	font-weight: 600;
	padding: .5rem .75rem;
}

.statistics-map-canvas {
	background: $lightest-grey;
	position: relative;

	img {
		display: block;
		width: 100%;
	}
}

.statistics-map-modal {
	.modal-card {
		margin: 0 auto;
		max-height: 78vh;
		max-width: 880px;
		width: 78vw;
	}

	.modal-card-head {
		background: $dark;
		border-bottom: 0;
		padding: .75rem 1rem;
	}

	.modal-card-title {
		color: white;
		font-size: 20px;
		font-weight: 600;
	}

	.modal-card-body {
		align-items: center;
		background: $light;
		display: flex;
		justify-content: center;
		height: calc(78vh - 58px);
		overflow: hidden;
		padding: 14px;
	}
}

.statistics-map-dialog-canvas {
	align-items: center;
	display: flex;
	height: 100%;
	justify-content: center;
	position: relative;
	width: 100%;

	img {
		display: block;
		height: 100%;
		object-fit: contain;
		width: 100%;
	}
}

.statistics-map-marker {
	align-items: center;
	background: $white;
	border: 2px solid $info;
	border-radius: 50%;
	box-shadow: 0 0 0 3px rgba(253, 126, 20, .24), 0 2px 10px rgba(0, 0, 0, .22);
	display: flex;
	height: 34px;
	justify-content: center;
	padding: 5px;
	position: absolute;
	transform: translate(-50%, 50%);
	width: 34px;
	z-index: 2;
}

.statistics-map-inventory-icon {
	background-position: center;
	background-repeat: no-repeat;
	background-size: contain;
	height: 22px;
	width: 22px;
}

.statistics-flaw-header,
.statistics-flaw-row {
	margin-left: 0;
	margin-right: 0;
	width: 100%;
}

.flaw-name {
	justify-content: flex-start;
}

.flaw-date {
	max-width: 150px;
}

.flaw-status {
	max-width: 130px;
}

.statistics-structure-select {
	position: relative;
	width: 100%;
}

.structure-select-trigger {
	align-items: center;
	background: white;
	border: 1px solid $lighter-grey;
	border-radius: 4px;
	color: $dark;
	cursor: pointer;
	display: flex;
	font-size: 14px;
	height: 38px;
	justify-content: space-between;
	padding: 0 12px;
	text-align: left;
	width: 100%;

	span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	i {
		color: $primary;
		margin-left: 8px;
		transition: transform .16s ease;

		&.open {
			transform: rotate(180deg);
		}
	}
}

.structure-select-menu {
	background: white;
	border: 1px solid $lighter-grey;
	border-radius: 6px;
	box-shadow: 0 12px 26px rgba(0, 0, 0, .14);
	left: 0;
	margin-top: 6px;
	overflow: hidden;
	position: absolute;
	top: 100%;
	width: 380px;
	z-index: 10;
}

.structure-search-input {
	border-radius: 4px;
	font-size: 14px;
	height: 38px;
	margin: 10px;
	width: calc(100% - 20px);
}

.structure-options {
	border-top: 1px solid $lighter-grey;
	max-height: 420px;
	overflow-y: auto;
	padding: 0 10px 8px;
}

.structure-option {
	align-items: center;
	border-bottom: 1px solid $lighter-grey;
	color: $dark;
	cursor: pointer;
	display: flex;
	font-size: 14px;
	min-height: 44px;
	padding-right: 10px;

	&:hover,
	&.selected {
		background: $lightest-grey;
	}

	&.selected {
		font-weight: bold;
	}
}

.structure-option-name {
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.structure-option-icon {
	color: $primary;
	margin-left: 8px;
	width: 16px;
}

.structure-empty {
	color: $light-grey;
	font-size: 14px;
	padding: 14px;
}

.column.info {
  max-width: 300px;
  .info-box {
    background-color: $white;
    width: 100%;
    padding: 0.5rem;
  }
  &.mobile {
    max-width: 100%;
  }
}
.info-text-box {
  background-color: white;
  border: 1px solid $multiselect-border-color;
  padding: 4px 8px;
  font-size: 14px;
  white-space: pre-line;
}


li {
  &.is-active {
    a {
      background-color: $lightest-grey !important;
      border-color: $lighter-grey !important;
    }
    p {
      color: $dark;
    }
  }
}
.panel-header-item.narrow {
  max-width: 46px;
  text-align: center;
}
.panel-circle {
  min-width: 46px;
  text-align: center;
  i {
    &.green {
      color: #7bca78 !important;
    }
    &.yellow {
      color: #ffe000 !important;
    }
    &.red {
      color: #da6060 !important;
    }
    &.grey {
      color: lightgray !important;
    }
    &.orange {
        color: #ff9500;
    }
  }
}
</style>

<style lang="scss">
.multiselect.panel-select.year {
  .caret {
    background-color: #fcfcfc;
  }
  .multiselect__tags {
    background-color: white;

    .multiselect__single,
    .multiselect__input {
      background-color: white;
    }
  }
  .multiselect__content {
    background: white;
  }
}

 @media screen and (max-width: 600px) {
  .mobile_statistics {
    font-size: 9px !important;
    &__header {
      min-width: 46px !important;
    }
  }
  .mobile-data {
    font-size: 9px !important;
    padding-top: 5px !important;
    min-width: 46px !important;
  }

  .mobile-data__list {
    width: 100% !important;
    min-width: 0px !important;
  }
}

@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  body * {
    visibility: hidden;
  }

  .statistics-page,
  .statistics-page * {
    visibility: visible;
  }

  .statistics-page {
    background: white !important;
    color: #111 !important;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .statistics-page .tabs,
  .statistics-page .statistics-filter-panel,
  .statistics-page button,
  .statistics-page .button,
  .statistics-page .statistics-map-action,
  .statistics-page .statistics-map-modal,
  .statistics-page .structure-select-menu {
    display: none !important;
  }

  .statistics-overview,
  .statistics-search-column,
  .statistics-compare-layout,
  .statistics-detail-layout {
    display: block !important;
    gap: 0 !important;
    height: auto !important;
    min-height: 0 !important;
  }

  .statistics-chart-panel,
  .statistics-inventory-panel,
  .statistics-flaw-panel {
    background: white !important;
    border: 1px solid #bbb !important;
    box-shadow: none !important;
    color: #111 !important;
    margin-bottom: 14px !important;
    overflow: visible !important;
    page-break-inside: avoid;
  }

  .legend-box.handled,
  .building-chart-segment.handled {
    background: #7bca78 !important;
    color: #fff !important;
  }

  .legend-box.unhandled,
  .building-chart-segment.unhandled {
    background: #da6060 !important;
    color: #fff !important;
  }

  .building-chart-track {
    border: 1px solid #777 !important;
  }

  .building-chart-segment {
    border: 1px solid rgba(0, 0, 0, .18) !important;
    font-weight: 700 !important;
    text-shadow: none !important;
  }

  .statistics-filter-panel .label,
  .statistics-chart-title .list-title,
  .statistics-panel-title .list-title,
  .building-chart-label,
  .statistics-muted,
  .statistics-title-range {
    color: #111 !important;
  }

  .statistics-detail-panel--expanded,
  .statistics-detail-layout--has-expanded-panel,
  .statistics-overview-detail,
  .statistics-compare-layout.active .statistics-detail-layout {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .building-chart-row,
  .panel-list,
  .panel-list-header {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .statistics-inventory-header,
  .statistics-inventory-row {
    display: grid !important;
    grid-template-columns: minmax(0, 50%) minmax(0, 25%) minmax(0, 12.5%) minmax(0, 12.5%) !important;
    gap: 14px !important;
  }

  .statistics-inventory-row--group {
    grid-template-columns: minmax(0, 50%) minmax(0, 25%) minmax(0, 25%) !important;
  }

  .statistics-inventory-row--group .inventory-name {
    grid-column: 1 !important;
  }

  .statistics-inventory-row--group .inventory-count {
    grid-column: 2 !important;
  }

  .statistics-inventory-row--group .narrow-count {
    grid-column: 3 !important;
  }

  .statistics-inventory-row--child {
    grid-template-columns: minmax(0, 50%) minmax(0, 40%) minmax(0, 10%) !important;
  }

  .statistics-inventory-row .inventory-name--group {
    grid-column: auto !important;
  }

  .statistics-inventory-row .inventory-building {
    display: flex !important;
  }

  .statistics-inventory-header .panel-header-item,
  .statistics-inventory-row .panel-list-item {
    align-items: center !important;
    display: flex !important;
    justify-content: flex-start !important;
    max-width: none !important;
    min-width: 0 !important;
    width: auto !important;
  }

  .statistics-inventory-header .inventory-count,
  .statistics-inventory-header .narrow-count,
  .statistics-inventory-row .inventory-count,
  .statistics-inventory-row .narrow-count {
    justify-content: flex-end !important;
    text-align: right !important;
  }
}
</style>
